import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setKnownMetaEmail } from '../lib/gtag';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ein Fehler ist aufgetreten";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      setUser(data && data.authenticated === false ? null : data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Meta Event Match Quality: bekannte E-Mail des eingeloggten Users hinterlegen (nur mit Consent).
  useEffect(() => {
    setKnownMetaEmail(user?.email || null);
  }, [user]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const register = async (email, password, name, phone, userType = 'private', inviteCode = undefined, businessProfile = undefined, privacyAccepted = false) => {
    try {
      const payload = { email, password, name, phone, user_type: userType, privacy_accepted: privacyAccepted };
      if (inviteCode) payload.invite_code = inviteCode;
      if (businessProfile) {
        payload.company_name = businessProfile.company_name;
        payload.vat_id = businessProfile.vat_id;
        payload.company_address = businessProfile.company_address;
        payload.company_zip = businessProfile.company_zip;
        payload.company_city = businessProfile.company_city;
        payload.company_country = businessProfile.company_country;
        payload.employee_count = businessProfile.employee_count;
        payload.fleet_size = businessProfile.fleet_size;
        payload.website = businessProfile.website;
      }
      const { data } = await axios.post(`${BACKEND_URL}/api/auth/register`, payload, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (error) {
      return { success: false, error: formatApiErrorDetail(error.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
